<template>
  <VRow class="mt-0">
    <VCol>
      <div class="text-center">
        <h3 class="text-uppercase">
          {{ $t('auth.label.continueGuest') }}
        </h3>
        <p class="font14 text-center mb-6">
          {{ $t('auth.p.createAccount') }}
        </p>
        <ObservedForm
          :loading="loading"
          @submit="submit"
        >
          <TextFieldWithValidation
            v-model="formData.email"
            :label="$t('label.email')"
            rules="required|email|max:75"
            name="email"
            single-line
            required
            data-testid="auth-guest"
          />
          <SBtn
            :color="btnColor"
            type="submit"
            block
            action
            data-testid="auth-guest-checkout"
          >
            {{ $t('btn.label.checkout') }}
          </SBtn>
        </ObservedForm>
      </div>
    </VCol>
  </VRow>
</template>

<script lang="ts" setup>
import TextFieldWithValidation from '~/components/base/fields/TextFieldWithValidation.vue'
import ObservedForm from '~/components/base/ObservedForm.vue'
import { hasApiErrorCode } from '~/utils/http/api-error'

const emits = defineEmits<{
  (e: 'authenticated'): void
  (e: 'already-exists', username: string): void
}>()

interface Props {
 btnColor?: string
}

withDefaults(defineProps<Props>(), {
  btnColor: 'primary'
})

const formData = ref({
  email: ''
})

const { processAction, loading } = useAction()

const auth = useSAuth()
const submit = () => processAction(async () => {
  const date = new Date()
  const password = date
    .toString()
    .replace(/[\s,:]/g, '')
    .substr(0, 20)
  const payload = {
    customer: {
      email: formData.value.email,
      password
    }
  }

  try {
    await $api()('account/guest', { body: payload, method: 'post' })
  } catch (e) {
    // `$api` rejects with an ofetch FetchError, so the helper reads the code
    // off the response body.
    if (hasApiErrorCode(e, 'CustomerAlreadyExists')) {
      emits('already-exists', formData.value.email)
      return
    }
    throw e
  }

  await auth.login({ username: formData.value.email, password })
  emits('authenticated')
  // No status is excluded from the toast, which is this form's only error surface: the one
  // 409 with a surface of its own (CustomerAlreadyExists) returns above and never gets here.
}, () => null, true)

</script>
